import { toast } from 'react-hot-toast'
import { useNotificationStore, type NotificationType } from '@/stores/notificationStore'

// Expose react-hot-toast API for E2E test programmatic dismissal.
// Tests call window.__rht_toast__.remove() to bypass the hover-pause mechanism
// that prevents toasts from auto-dismissing when the mouse is over the toaster container.
if (typeof window !== 'undefined') {
  ;(window as unknown as Record<string, unknown>).__rht_toast__ = toast
}

interface NotifyOptions {
  duration?: number
  // If true, adds to notification alerts list when toast expires (default: true for error/warning, false for success/info)
  persist?: boolean
}

// Track manually dismissed toasts
const dismissedToasts = new Set<string>()

const createNotifyFunction = (
  type: NotificationType,
  toastFn: (message: string, options?: object) => string,
  defaultPersist: boolean,
  defaultDuration = 4000
) => {
  return (title: string, message?: string, options: NotifyOptions = {}) => {
    const { persist = defaultPersist, duration = defaultDuration } = options
    const toastMessage = message ? `${title}: ${message}` : title

    // Show toast and get its ID
    const toastId = toastFn(toastMessage, { duration })

    // If persist is enabled, add to notifications only if not manually dismissed
    if (persist) {
      setTimeout(() => {
        if (!dismissedToasts.has(toastId)) {
          useNotificationStore.getState().addNotification({
            type,
            title,
            message,
          })
        } else {
          // Clean up the tracking set
          dismissedToasts.delete(toastId)
        }
      }, duration)
    }

    return toastId
  }
}

// Function to manually dismiss a toast and mark it as dismissed
export const dismissToast = (toastId: string) => {
  dismissedToasts.add(toastId)
  toast.dismiss(toastId)
}

export const notify = {
  // Success and info: show toast only, don't persist to alerts list by default
  success: createNotifyFunction('success', toast.success, false),
  info: (title: string, message?: string, options: NotifyOptions = {}) => {
    const { persist = false, duration = 4000 } = options
    const toastMessage = message ? `${title}: ${message}` : title

    const toastId = toast(toastMessage, {
      icon: '\u2139\ufe0f',
      duration,
    })

    if (persist) {
      setTimeout(() => {
        if (!dismissedToasts.has(toastId)) {
          useNotificationStore.getState().addNotification({
            type: 'info',
            title,
            message,
          })
        } else {
          dismissedToasts.delete(toastId)
        }
      }, duration)
    }

    return toastId
  },
  // Error and warning: show toast AND persist to alerts list by default (only if not dismissed)
  error: createNotifyFunction('error', toast.error, true, 4000),
  warning: (title: string, message?: string, options: NotifyOptions = {}) => {
    const { persist = true, duration = 4000 } = options
    const toastMessage = message ? `${title}: ${message}` : title

    const toastId = toast(toastMessage, {
      icon: '\u26a0\ufe0f',
      duration,
    })

    if (persist) {
      setTimeout(() => {
        if (!dismissedToasts.has(toastId)) {
          useNotificationStore.getState().addNotification({
            type: 'warning',
            title,
            message,
          })
        } else {
          dismissedToasts.delete(toastId)
        }
      }, duration)
    }

    return toastId
  },
  // Export dismiss function for use in toast UI
  dismiss: dismissToast,
}
